import { MessageRole, ToolCallStatus } from "@/components/VibeChat/types";
import { ChatMessage, NavigationItem, ToolCallData } from "@/components/VibeChat/stores/types";
import { ChatMessageI } from "@/components/Playground/types";
import { CONTENT_TYPES, ROLES } from "@/types/prompt-blueprint";

export interface ParsedToolCall {
  tool_name: string;
  tool_input: Record<string, unknown>;
}

export const parseToolCallsFromJson = (jsonString: string): ParsedToolCall[] | null => {
  try {
    const parsed = JSON.parse(jsonString);
    if (parsed && Array.isArray(parsed.tool_calls)) {
      return parsed.tool_calls;
    }
  } catch {
    // nothing
  }
  return null;
};

/** Extracts navigation from a tool result JSON string (API tool role messages are tool results). */
export const parseNavigationFromToolResultContent = (
  content: string,
): NavigationItem[] | undefined => {
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") {
      return undefined;
    }
    if (Array.isArray(parsed.navigation) && parsed.navigation.length > 0) {
      return parsed.navigation as NavigationItem[];
    }
    const { results } = parsed;
    if (results && typeof results === "object" && !Array.isArray(results)) {
      const nav = (results as Record<string, unknown>).navigation;
      if (Array.isArray(nav) && nav.length > 0) {
        return nav as NavigationItem[];
      }
    }
  } catch {
    // not JSON
  }
  return undefined;
};

export const parseToolCallWithNavigation = (toolCall: {
  id: string;
  type: string;
  function: { name: string; arguments: string };
}): ToolCallData => {
  let toolInput: Record<string, unknown> = {};
  try {
    toolInput = JSON.parse(toolCall.function.arguments);
  } catch {
    toolInput = { raw: toolCall.function.arguments };
  }

  let navigation: NavigationItem[] | undefined;
  if (toolCall.function.name === "return_results" && toolInput.results) {
    const results = toolInput.results as Record<string, unknown>;
    if (results.navigation && Array.isArray(results.navigation)) {
      navigation = results.navigation as NavigationItem[];
    }
  }

  return {
    id: toolCall.id,
    name: toolCall.function.name,
    input: toolInput,
    status: ToolCallStatus.COMPLETED,
    completedAt: Date.now(),
    navigation,
  };
};

export const extractComputerAgentDescription = (argumentsStr: string): string | null => {
  try {
    const args = JSON.parse(argumentsStr);
    return args.task_description_human_readable || "Computer agent";
  } catch {
    return null;
  }
};

export const convertComputerAgentStepsToMessages = (
  steps: {
    type: "text" | "tool_call" | "tool_result";
    content?: string;
    tool_name?: string;
    tool_input?: Record<string, unknown>;
    tool_call_id?: string;
  }[],
): string[] => {
  const messages: string[] = [];

  for (const step of steps) {
    if (step.type === "text" && step.content) {
      messages.push(step.content);
    } else if (step.type === "tool_call" && step.tool_name) {
      const toolInputStr = step.tool_input ? JSON.stringify(step.tool_input, null, 2) : "";
      messages.push(
        `Using ${step.tool_name}${toolInputStr ? `:\n\`\`\`json\n${toolInputStr}\n\`\`\`` : ""}`,
      );
    } else if (step.type === "tool_result" && step.content) {
      try {
        const parsedContent = JSON.parse(step.content);
        messages.push(`Result: ${JSON.stringify(parsedContent, null, 2)}`);
      } catch {
        messages.push(`Result: ${step.content}`);
      }
    }
  }

  return messages;
};

export const parseSSEEventsFromBuffer = (
  buffer: string,
  onEvent: (line: string) => void,
): string => {
  let remainingBuffer = buffer;
  let newlineIndex;
  while ((newlineIndex = remainingBuffer.indexOf("\n\n")) !== -1) {
    const line = remainingBuffer.slice(0, newlineIndex);
    remainingBuffer = remainingBuffer.slice(newlineIndex + 2);
    if (!line.trim()) {
      continue;
    }

    if (line.startsWith("data: ")) {
      onEvent(line);
    }
  }
  return remainingBuffer;
};

export const convertToChatMessageI = (message: ChatMessage, index: number): ChatMessageI => {
  const role = message.role === MessageRole.USER ? ROLES.USER : ROLES.ASSISTANT;
  const id = message.taskId || `vibe-${index}-${Date.now()}`;
  const created_at = new Date().toISOString();

  const content: ChatMessageI["content"] = [];

  // Add text content if present
  if (message.content) {
    content.push({
      type: CONTENT_TYPES.TEXT,
      text: message.content,
    });
  }

  return {
    id,
    role,
    content,
    individual_run_request_id: 0,
    created_at,
    error: message.isError
      ? {
          type: "error",
          message: message.errorMessage || "An error occurred",
        }
      : undefined,
  };
};
